import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { waLink } from "@/siteConfig";
import { Chip } from "@/components/bits";
import { useLang } from "@/i18n";

const TYPES = ["fi.type.pe", "fi.type.fo", "fi.type.corp", "fi.type.dev", "fi.type.other"];
const BASES = ["fi.base.mum", "fi.base.blr", "fi.base.del", "fi.base.gulf", "fi.base.other"];
const INTERESTS = ["tl.cls.land", "tl.cls.hosp", "tl.cls.health", "tl.cls.edu", "tl.cls.agri"];
const CAPS = ["fi.cap.1", "fi.cap.2", "fi.cap.3", "fi.cap.4", "fi.cap.5"];

export const InstitutionalForm = ({ presetInterest }) => {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [firm, setFirm] = useState("");
  const [type, setType] = useState("");
  const [based, setBased] = useState("");
  const [interests, setInterests] = useState([]);
  const [capacity, setCapacity] = useState("");
  const [thesis, setThesis] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (presetInterest && !interests.includes(presetInterest)) {
      setInterests((prev) => [...prev, presetInterest]);
    }
  }, [presetInterest]);

  const missing = { name: !name.trim(), firm: !firm.trim() };
  const err = (k) => attempted && missing[k];

  const toggleInterest = (k) =>
    setInterests((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const lines = [
    t("wa.in.title"),
    "—",
    `${t("wa.name")}: ${name.trim()}`,
    `${t("wa.firm")}: ${firm.trim()}`,
  ];
  if (type) lines.push(`${t("wa.type")}: ${t(type)}`);
  if (based) lines.push(`${t("wa.based")}: ${t(based)}`);
  if (interests.length) lines.push(`${t("wa.interests")}: ${interests.map(t).join(", ")}`);
  if (capacity) lines.push(`${t("wa.capacity")}: ${t(capacity)}`);
  if (thesis.trim()) lines.push(`${t("wa.thesis")}: ${thesis.trim()}`);
  const url = waLink(lines.join("\n"));

  const lineCls = (error) =>
    `w-full bg-transparent border-b outline-none py-2.5 text-[#EDE7DA] placeholder-[#EDE7DA]/25 transition-colors duration-300 ${
      error ? "border-[#B8894B]" : "border-[#1A2A24] focus:border-[#B8894B]"
    }`;

  const Label = ({ children, error }) => (
    <span
      className={`font-mono-label text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${
        error ? "text-[#B8894B]" : "text-[#EDE7DA]/45"
      }`}
    >
      {children}
      {error ? ` ${t("lc.required")}` : ""}
    </span>
  );

  const submit = () => {
    setAttempted(true);
    if (Object.values(missing).some(Boolean)) return;
    window.open(url, "_blank");
    setSent(true);
  };

  if (sent) {
    return (
      <div data-testid="institutional-form-confirmation" className="py-4">
        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#B8894B]">
          <Check className="w-5 h-5 text-[#B8894B]" />
        </span>
        <h3 className="font-display text-2xl text-[#EDE7DA] tracking-tight mt-6">
          {t("fi.done.h")}
        </h3>
        <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-3">
          {t("fi.done.body")}
        </p>
        <a
          data-testid="institutional-form-fallback-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-7 font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#B8894B] border-b border-[#B8894B]/40 pb-1 hover:text-[#EDE7DA] hover:border-[#EDE7DA]/40 transition-colors duration-300"
        >
          {t("lc.done.fallback")}
        </a>
      </div>
    );
  }

  return (
    <div className="grid gap-7 mt-8">
      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <Label error={err("name")}>{t("fi.name")}</Label>
          <input
            data-testid="inst-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("ph.name")}
            className={`mt-2 ${lineCls(err("name"))}`}
          />
        </div>
        <div>
          <Label error={err("firm")}>{t("fi.firm")}</Label>
          <input
            data-testid="inst-firm-input"
            value={firm}
            onChange={(e) => setFirm(e.target.value)}
            placeholder="—"
            className={`mt-2 ${lineCls(err("firm"))}`}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <Label>{t("fi.type")}</Label>
          <div className="flex flex-wrap gap-2.5 mt-3">
            {TYPES.map((k) => (
              <Chip key={k} testId={`inst-type-${k.split(".").pop()}`} active={type === k} onClick={() => setType(k)}>
                {t(k)}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <Label>{t("fi.based")}</Label>
          <div className="flex flex-wrap gap-2.5 mt-3">
            {BASES.map((k) => (
              <Chip key={k} testId={`inst-based-${k.split(".").pop()}`} active={based === k} onClick={() => setBased(k)}>
                {t(k)}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      <div>
        <Label>{t("fi.interest")}</Label>
        <div className="flex flex-wrap gap-2.5 mt-3">
          {INTERESTS.map((k) => (
            <Chip
              key={k}
              testId={`inst-interest-${k.split(".").pop()}`}
              active={interests.includes(k)}
              onClick={() => toggleInterest(k)}
            >
              {t(k)}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <Label>{t("fi.capacity")}</Label>
        <div className="flex flex-wrap gap-2.5 mt-3">
          {CAPS.map((k) => (
            <Chip key={k} testId={`inst-capacity-${k.split(".").pop()}`} active={capacity === k} onClick={() => setCapacity(k)}>
              {t(k)}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <Label>{t("fi.thesis")}</Label>
        <textarea
          data-testid="inst-thesis-input"
          value={thesis}
          onChange={(e) => setThesis(e.target.value)}
          placeholder={t("lc.notesPh")}
          rows={3}
          className="mt-2 w-full bg-transparent border border-[#1A2A24] focus:border-[#B8894B] outline-none p-4 text-sm text-[#EDE7DA] placeholder-[#EDE7DA]/25 transition-colors duration-300 resize-none"
        />
      </div>

      <div>
        <button
          type="button"
          data-testid="institutional-form-submit"
          onClick={submit}
          className="w-full font-mono-label text-xs tracking-[0.2em] uppercase bg-[#B8894B] text-[#0E1613] px-7 py-4 hover:bg-[#EDE7DA] transition-colors duration-300"
        >
          {t("fi.submit")}
        </button>
        <p className="font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40 mt-4 text-center">
          {t("cta.waNote")}
        </p>
      </div>
    </div>
  );
};
